if (!customElements.get('swiper-carousel')) {
  /**
   * @class SwiperCarousel
   * @classdesc A custom element for creating a Swiper carousel
   * @extends HTMLElement
   */
  class SwiperCarousel extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * Initializes the element.
     */
    init() {
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.prevButton =
        this.querySelector('[data-prev]') ??
        this.parentElement?.querySelector('[data-prev]');
      this.nextButton =
        this.querySelector('[data-next]') ??
        this.parentElement?.querySelector('[data-next]');
      this.events = new Map();
      this.pagination = this.querySelector('.swiper-pagination');
      this.navList = document.getElementById(this.dataset.navList);
      this.navListItems = Array.from(
        this.navList?.querySelectorAll('[data-nav-link]') ?? [],
      );
      this.isActive = false;
      this.activeParams = {};
      this.attributeOptions = [
        'allowTouchMove',
        'autoHeight',
        'autoplay',
        'centeredSlides',
        'controller',
        'effect',
        'loop',
        'pagination',
        'slidesPerView',
        'slidesPerGroup',
        'spaceBetween',
        'speed',
        'watchSlidesProgress',
      ];

      this.onBreakpointToggle = this.handleBreakpointToggle.bind(this);
      this.onNavListClick = this.handleNavListClick.bind(this);
      this.breakpointAutoSlides = false;
      this.watchBreakpoints = false;
    }

    /**
     * Gets the breakpoint media query
     *
     * @returns {MediaQueryList|null} The breakpoint media query
     */
    getBreakpoint() {
      if (!this.dataset.breakpoint) return null;

      const operatorMap = {
        '<': 'max',
        '>': 'min',
      };
      const breakpointString = this.dataset.breakpoint;
      const operator = ['<', '>'].includes(breakpointString[0])
        ? operatorMap[breakpointString[0]]
        : 'min';
      const value =
        parseInt(breakpointString.replace(/[<>]/, ''), 10) -
        (operator === 'max' ? 1 : 0);
      const query = `(${operator}-width: ${value}px)`;

      return window.matchMedia(query);
    }

    /**
     * Gets the options
     *
     * @returns {Object} The options
     */
    getOptions() {
      const options = { loop: true, on: {} };
      const breakpointKeys = [0, 750, 990, 1200];
      let breakpointOptions = [{}, {}, {}, {}];

      for (const option of this.attributeOptions) {
        if (typeof this.dataset[option] !== 'undefined') {
          if (this.dataset[option].includes('|')) {
            let values = this.dataset[option]
              .split('|')
              .map(value => this.parseOption(value, option));

            options[option] = values[0];
            // Ensure values exist for all breakpoints
            if (values.length < breakpointKeys.length) {
              values = Object.assign(
                new Array(breakpointKeys.length).fill(
                  values[values.length - 1],
                ),
                values,
              );
            }

            for (let i = 0; i < values.length; i++) {
              breakpointOptions[i][option] = values[i];
            }
          } else {
            options[option] = this.getOption(option);
          }

          if (option === 'effect' && this.dataset[option] === 'fade')
            options.fadeEffect = { crossFade: true };
        }
      }

      const breakpoints = {};
      for (let i = 0; i < breakpointKeys.length; i++) {
        if (breakpointOptions[i]) {
          breakpoints[breakpointKeys[i]] = breakpointOptions[i];
        }
      }

      options.breakpoints = breakpoints;

      if (this.prevButton && this.nextButton)
        options.navigation = {
          nextEl: this.nextButton,
          prevEl: this.prevButton,
          disabledClass: 'is-disabled',
        };
      if (this.pagination) {
        options.pagination = {
          ...(options.pagination || {}),
          el: this.pagination,
          clickable: true,
        };
      }

      if (this.navList) {
        options.on.activeIndexChange = s => {
          const { activeIndex, realIndex } = s;
          const index = s.params.loop ? realIndex : activeIndex;

          this.navListItems.forEach(item =>
            item.setAttribute('aria-current', item.dataset.index == index),
          );
        };
      }

      if (this.dataset.autoHeight?.includes('|')) {
        this.addBreakpointParamReset('autoHeight');
      }

      if (this.dataset.slidesPerView?.includes('auto')) {
        options.watchSlidesProgress = true;

        if (this.dataset.slidesPerView.includes('|')) {
          this.addBreakpointParamReset('slidesPerView', 'auto');
        }
      }

      if (this.wrapper.querySelector('accordion-item')) {
        this.addEventCallback(
          'slideChangeTransitionStart',
          this.collapseAccordionOnLeave,
        );
        this.autoCollapseAccordions = true;
      }

      for (const [event, callbacks] of this.events) {
        const eventCallback = (...args) => {
          if (!this.isActive) return;

          for (const callback of callbacks) {
            callback(...args);
          }
        };

        options.on[event] = eventCallback;
      }

      return options;
    }

    /**
     * Collapses accordions on slide change transition start
     *
     * @param {Event} e The event
     */
    collapseAccordionOnLeave(e) {
      const openAccordionSlides = Array.from(
        this.wrapper.querySelectorAll(
          '.swiper-slide:has(accordion-item[open])',
        ),
      );

      openAccordionSlides.forEach(slide => {
        if (!slide.classList.contains('swiper-slide-visible')) {
          slide.querySelector('accordion-item').close();
        }
      });
    }

    /**
     * Parameter change callback for breakpoint-based param updates.
     *
     * @param {string} key The parameter name
     * @param {Object} [options] Callback options
     * @param {boolean} [options.condition] The condition required to trigger the reset
     * @param {Function} [options.callback] The callback to trigger
     * @param {Swiper} swiper The swiper instance
     */
    onBreakpointParamChange(
      key,
      { condition = true, callback = this.delayedReset.bind(this) } = {},
      swiper,
    ) {
      const param = {
        previous: this.getPreviousParamValue(key),
        current: swiper.params[key],
      };

      if (param.previous !== param.current) {
        this.activeParams[key] = param.current;

        if (callback) {
          if (typeof condition === 'function') {
            condition = condition(param, swiper);
          }

          if (condition) callback(param, swiper);
        }
      }
    }

    /**
     * Resets the slider after a delay.
     */
    delayedReset() {
      setTimeout(() => this.resetSlider());
    }

    /**
     * Returns the previous value of a parameter, falling back to default values if the parameter is not set.
     *
     * @param {string} key The parameter name
     * @returns {any} The previous value of the parameter
     */
    getPreviousParamValue(key) {
      return (
        this.activeParams[key] ??
        this.options.breakpoints[this.initialBreakpoint]?.[key] ??
        this.options[key]
      );
    }

    /**
     * Adds an event callback
     *
     * @param {string} event The event name
     * @param {Function} callback The event callback
     */
    addEventCallback(event, callback) {
      const existingCallbacks = this.events.get(event) ?? [];
      const callbacks = [...existingCallbacks, callback.bind(this)];
      this.events.set(event, callbacks);
    }

    /**
     * Adds an event callback to trigger a parameter value reset with an optional value condition.
     *
     * @param {string} key The param key
     * @param {any} [value] The param value required to trigger the reset
     */
    addBreakpointParamReset(key, value) {
      const valueArray = Array.isArray(value) ? value : [value];
      const condition = value
        ? param => valueArray.includes(param.current)
        : true;

      this.addEventCallback(
        'breakpoint',
        this.onBreakpointParamChange.bind(this, key, { condition }),
      );
    }

    /**
     * Retrieves an option value.
     *
     * @param {string} key The option key
     * @returns {any} The option value
     */
    getOption(key) {
      const value = this.dataset[key];

      return this.parseOption(value, key);
    }

    /**
     * Parses a given option value.
     *
     * @param {string} value The option value.
     * @param {string} key The option key.
     * @returns {any} The parsed option value.
     */
    parseOption(value, key) {
      if (value === 'true' || value === '') return true;
      if (value === 'false') return false;

      if (key === 'autoplay') return { delay: parseInt(value, 10) };
      if (key === 'controller') return this.configureController(value);
      if (key === 'pagination') return { type: value };

      if (/^\d+$/.test(value)) return parseInt(value, 10);

      return value;
    }

    /**
     * Configures control of slave Swiper instances.
     *
     * @param {string} controller - A JSON string containing the IDs of the Swiper instances to control.
     * @returns {Object} The controller configuration object.
     */
    configureController(controller) {
      if (!controller) return null;

      const control = JSON.parse(controller).map(
        slave => document.getElementById(slave)?.swiper,
      );

      const by = this.dataset.controlBy ?? 'slide';
      const inverse = this.dataset.controlInverse ?? false;

      return { control, by, inverse };
    }

    connectedCallback() {
      this.init();
      this.options = this.getOptions();
      this.breakpoint = this.getBreakpoint();

      this.handleBreakpointToggle();

      this.breakpoint?.addEventListener('change', this.onBreakpointToggle);
      this.navList?.addEventListener('click', this.onNavListClick);
    }

    disconnectedCallback() {
      this.destroySlider();

      this.breakpoint?.removeEventListener('change', this.onBreakpointToggle);
      this.navList?.removeEventListener('click', this.onNavListClick);
    }

    /**
     * Initializes the Swiper carousel.
     */
    initSlider() {
      this.swiper = new Swiper(this.querySelector('.swiper'), this.options);
      this.initialBreakpoint = this.swiper.currentBreakpoint;

      this.isActive = true;
    }

    /**
     * Destroys the Swiper carousel.
     */
    destroySlider() {
      this.swiper?.destroy(true, true);
      this.initialBreakpoint = null;
      this.isActive = false;
    }

    /**
     * Resets the Swiper carousel.
     */
    resetSlider() {
      this.destroySlider();
      this.initSlider();
    }

    /**
     * Enables or disables carousel based on value of breakpoint option.
     */
    handleBreakpointToggle() {
      const breakpointMatch = this.breakpoint?.matches ?? true;

      if (breakpointMatch && !this.isActive) {
        this.initSlider();
      } else if (!breakpointMatch && this.isActive) {
        this.destroySlider();
      }
    }

    /**
     * Handles a click on an item in the navigation list.
     *
     * @param {Event} e - The event.
     */
    handleNavListClick(e) {
      if (e.target.dataset.navLink !== undefined) {
        if (this.swiper.params.loop) {
          this.swiper.slideToLoop(e.target.dataset.index);
        } else {
          this.swiper.slideTo(e.target.dataset.index);
        }
      }
    }
  }

  customElements.define('swiper-carousel', SwiperCarousel);
}
